import { useState, useEffect, useRef } from "react";
export default function TrainingStory() {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <section ref={ref} style={{
    backgroundPosition: "0% 0%",
    backgroundRepeat: "repeat",
    color: "rgb(244, 244, 243)",
    fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
    animation: "none 0s ease 0s 1 normal none running",
    backgroundColor: "#0b0b0d"
  }} className="w-full">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .story-card {
          background: #141417;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 16px;
          padding: 26px;
          transition: border-color 160ms, background 160ms, transform 160ms;
        }
        .story-card:hover {
          border-color: rgba(255,255,255,0.16);
          background: #1a1a1e;
          transform: translateY(-2px);
        }
        .story-link:hover {
          opacity: 0.75;
        }
      `}</style>
      <div className="page-container" style={{
      paddingTop: "132px",
      paddingBottom: "132px"
    }}>
        <div className={`flex flex-col md:flex-row items-center gap-12 md:gap-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex-1 min-w-0 w-full md:w-auto">
            <div className="flex flex-row items-center mb-4" style={{
            gap: "8px"
          }}>
              <span style={{
              display: "inline-block",
              width: "10px",
              height: "10px",
              minWidth: "10px",
              backgroundColor: "#f5c518",
              flexShrink: 0
            }} />
              <span style={{
              fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: "11px",
              fontWeight: 500,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#76767d"
            }}>
                THE STORY
              </span>
            </div>

            <h2 style={{
            color: "rgb(244, 244, 243)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "41.6px",
            fontWeight: "600",
            lineHeight: "43.264px",
            letterSpacing: "-1.248px",
            border: "0px none rgb(244, 244, 243)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "24px"
          }}>
              88 hours. One laptop.
              <br />
              No datacenter.
            </h2>

            <p style={{
            color: "rgb(185, 185, 189)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "17px",
            fontWeight: "400",
            lineHeight: "27.2px",
            letterSpacing: "-0.187px",
            border: "0px none rgb(185, 185, 189)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "32px",
            maxWidth: "480px"
          }}>
              Every training stage ran on a single RTX 3050 laptop — 4 GB of VRAM, 16 GB of RAM, Windows 11. SFT, then two rounds of ORPO alignment, with memory tricks that made it possible to fine-tune a 2B model on hardware most people already own.
            </p>

            <a href="#" className="story-link inline-block cursor-pointer transition-opacity duration-150" style={{
            color: "rgb(244, 244, 243)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "15.36px",
            fontWeight: "540",
            lineHeight: "24.576px",
            letterSpacing: "-0.1536px",
            borderRadius: "10px",
            border: "1px solid rgba(0, 0, 0, 0)",
            padding: "11.0592px 4.608px",
            animation: "none 0s ease 0s 1 normal none running",
            textDecoration: "none"
          }}>
              Read the full story →
            </a>
          </div>

          <div className="w-full md:flex-1 md:min-w-0" style={{
          maxWidth: "480px"
        }}>
            <div className="story-card">
              <div className="flex flex-row items-center" style={{
              marginBottom: "16px",
              gap: "8px"
            }}>
                <div className="flex flex-row items-center" style={{
                gap: "6px"
              }}>
                  <span style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#FF5F57"
                }} />
                  <span style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#FEBC2E"
                }} />
                  <span style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#28C840"
                }} />
                </div>
                <span style={{
                fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: "12px",
                fontWeight: "400",
                color: "#76767d",
                marginLeft: "8px"
              }}>
                  training_run.log
                </span>
              </div>

              <div style={{
              fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: "13px",
              lineHeight: "22px",
              whiteSpace: "pre",
              overflowX: "auto"
            }}>
                <div style={{
                color: "#76767d"
              }}>
                  {"# single RTX 3050 · 4 GB VRAM · no cloud"}
                </div>
                <div style={{
                height: "8px"
              }} />
                <div style={{
                color: "rgb(244, 244, 243)"
              }}>
                  <span style={{
                  fontWeight: 700
                }}>SFT-A</span>
                  {"   LoRA r=16   reasoning        ~1 h"}
                </div>
                <div style={{
                color: "rgb(244, 244, 243)"
              }}>
                  <span style={{
                  fontWeight: 700
                }}>SFT-B</span>
                  {"   LoRA r=16   5,500 samples    ~88 h"}
                </div>
                <div style={{
                color: "rgb(244, 244, 243)"
              }}>
                  <span style={{
                  fontWeight: 700
                }}>ORPO-2</span>
                  {"  LoRA r=8    2,038 pairs      ~26 h"}
                </div>
                <div style={{
                color: "rgb(244, 244, 243)"
              }}>
                  <span style={{
                  fontWeight: 700
                }}>ORPO-3</span>
                  {"  LoRA r=8    2,104 pairs      ~54 h"}
                </div>
                <div style={{
                height: "8px"
              }} />
                <div>
                  <span style={{
                  color: "#76767d"
                }}>{"# total cloud GPUs used: "}</span>
                  <span style={{
                  color: "#f5c518"
                }}>0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}